import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft } from 'lucide-react';
import PrivacyContent from '../components/Legal/PrivacyContent';
import { JsonLd } from '../components/Common/JsonLd';

/**
 * Standalone /privacy permalink.
 *
 * Rationale:
 *   Google's OAuth verification form requires a DEDICATED, scrapeable
 *   URL for the privacy policy — they don't accept tab-style SPAs
 *   like /legal where the privacy section is gated behind client
 *   state. This page renders just the privacy policy body with
 *   `index,follow` SEO so it's instantly accessible to Google's
 *   verification crawler and any external auditor.
 *
 *   /legal continues to exist as a unified hub for users who land
 *   from the in-app footer — both routes share the same content
 *   component (PrivacyContent), so there's no duplication.
 */
export default function Privacy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <Helmet>
        <title>Privacy Policy · HitzLink</title>
        <meta
          name="description"
          content="HitzLink privacy policy — what data we collect, how we use it, our Limited Use disclosure for Google API data, and how you can revoke access."
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://hitzlink.com/privacy" />
        <meta property="og:type" content="article" />
        <meta property="og:title" content="Privacy Policy · HitzLink" />
        <meta property="og:url" content="https://hitzlink.com/privacy" />
      </Helmet>

      {/* WebPage JSON-LD — gives Google an explicit signal that this
          is a policy page (not an article) so it's eligible for the
          "official" treatment in the SERP knowledge panel. */}
      <JsonLd
        id="privacy-webpage"
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': 'https://hitzlink.com/privacy',
          url: 'https://hitzlink.com/privacy',
          name: 'Privacy Policy',
          description:
            'HitzLink privacy policy — what data we collect, how we use it, our Limited Use disclosure for Google API data, and how you can revoke access.',
          inLanguage: 'th',
          isPartOf: { '@id': 'https://hitzlink.com#website' },
          publisher: { '@id': 'https://hitzlink.com#organization' },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hitzlink.com/' },
              { '@type': 'ListItem', position: 2, name: 'Privacy Policy', item: 'https://hitzlink.com/privacy' },
            ],
          },
        }}
      />

      <div className="bg-gray-900/50 backdrop-blur-sm border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm"
          >
            <ArrowLeft size={18} />
            <span>Back to Home</span>
          </button>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="bg-gray-900/30 backdrop-blur-sm rounded-lg border border-gray-800 p-6 sm:p-8">
          <PrivacyContent />
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-gray-400">
          <button
            onClick={() => navigate('/terms')}
            className="hover:text-white transition-colors"
          >
            Terms of Service
          </button>
          <button
            onClick={() => navigate('/legal')}
            className="hover:text-white transition-colors"
          >
            All legal documents
          </button>
          <a
            href="mailto:dpo@hitzlink.com"
            className="hover:text-white transition-colors"
          >
            Contact our DPO
          </a>
        </div>
      </main>

      <footer className="border-t border-gray-800 mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 text-center text-sm text-gray-400">
          <div>© {new Date().getFullYear()} Hitz Tech Solutions Co., Ltd. All rights reserved.</div>
          <div className="text-xs text-gray-500 mt-1">บริษัท ฮิตซ์เทค โซลูชั่นส์ จำกัด</div>
        </div>
      </footer>
    </div>
  );
}
